import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, Pill, Warehouse, ClipboardCheck, FileDown, LogOut, Cross, History, Settings,
  PanelLeftClose, PanelLeftOpen, Menu, X, Scale, ArrowLeftRight, AlertTriangle,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
  { to: "/master-obat", label: "Master Obat", icon: Pill, testid: "nav-master-obat-link", adminOnly: true },
  { to: "/tempat-penyimpanan", label: "Tempat Penyimpanan", icon: Warehouse, testid: "nav-lokasi-link", adminOnly: true },
  { to: "/stock-opname", label: "Stock Opname", icon: ClipboardCheck, testid: "nav-stock-opname-link" },
  { to: "/saldo-master", label: "Saldo Master", icon: Scale, testid: "nav-saldo-link" },
  { to: "/temuan", label: "Hasil Temuan SO", icon: AlertTriangle, testid: "nav-temuan-link" },
  { to: "/mutasi", label: "Mutasi Selama SO", icon: ArrowLeftRight, testid: "nav-mutasi-link", adminOnly: true },
  { to: "/riwayat", label: "Riwayat Opname", icon: History, testid: "nav-riwayat-link" },
  { to: "/laporan", label: "Laporan / Export", icon: FileDown, testid: "nav-laporan-link" },
  { to: "/pengaturan", label: "Pengaturan User", icon: Settings, testid: "nav-pengaturan-link", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [temuanCount, setTemuanCount] = useState(0);

  useEffect(() => { localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);

  useEffect(() => {
    api.get("/temuan")
      .then((r) => setTemuanCount((r.data.expiring_count || 0) + (r.data.missing_count || 0)))
      .catch(() => {});
  }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = nav.filter((n) => !n.adminOnly || user?.role === "admin");

  const SidebarBody = ({ mini }) => (
    <>
      <div className={`px-4 py-5 border-b border-slate-100 flex items-center ${mini ? "justify-center" : "gap-3"}`}>
        <div className="h-10 w-10 rounded-xl bg-teal-700 flex items-center justify-center shadow-sm shrink-0">
          <Cross className="h-5 w-5 text-white" strokeWidth={2.5} />
        </div>
        {!mini && (
          <div className="min-w-0">
            <div className="font-head font-extrabold text-slate-900 leading-tight text-[15px] truncate">StockOpname</div>
            <div className="text-[11px] text-slate-400 tracking-wide truncate">Farmasi Rumah Sakit</div>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => {
          const Icon = n.icon;
          const badge = n.to === "/temuan" ? temuanCount : 0;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              title={mini ? n.label : undefined}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `relative flex items-center ${mini ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              {!mini && <span className="flex-1 truncate">{n.label}</span>}
              {badge > 0 && (
                mini ? (
                  <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-white" data-testid="temuan-badge-dot" />
                ) : (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid="temuan-badge">{badge > 99 ? "99+" : badge}</span>
                )
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-slate-100">
        {!mini && (
          <div className="px-3 py-2 mb-2">
            <div className="text-[13px] font-semibold text-slate-800 truncate">{user?.name || "Admin"}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            <span className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${user?.role === "admin" ? "bg-teal-50 text-teal-700" : "bg-sky-50 text-sky-700"}`}>
              {user?.role === "admin" ? "Admin" : "Staff"}
            </span>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={doLogout}
          title={mini ? "Keluar" : undefined}
          className={`w-full flex items-center ${mini ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors duration-150`}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          {!mini && "Keluar"}
        </button>
      </div>
    </>
  );

  const sidebarW = collapsed ? "lg:w-20" : "lg:w-64";
  const mainML = collapsed ? "lg:ml-20" : "lg:ml-64";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex ${sidebarW} shrink-0 bg-white border-r border-slate-200 flex-col fixed h-screen z-30 transition-[width] duration-200`}>
        <SidebarBody mini={collapsed} />
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="absolute -right-3 top-20 h-6 w-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-teal-700 hover:border-teal-300"
        >
          {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-backdrop" />
          <aside className="relative w-64 bg-white border-r border-slate-200 flex flex-col h-full animate-in slide-in-from-left duration-200">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-4 text-slate-400 hover:text-slate-700" data-testid="mobile-close-button"><X className="h-5 w-5" /></button>
            <SidebarBody mini={false} />
          </aside>
        </div>
      )}

      <div className={`${mainML} min-h-screen transition-[margin] duration-200`}>
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-40 flex items-center gap-3 bg-white border-b border-slate-200 px-4 h-14">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" className="p-1.5 -ml-1.5 rounded-lg text-slate-600 hover:bg-slate-100">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-teal-700 flex items-center justify-center">
              <Cross className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-head font-extrabold text-slate-900 text-sm">StockOpname</span>
          </div>
        </div>

        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
